import React, { useCallback, useEffect, useMemo, useState } from "react";

const FIXED_AREAS = ["Motel", "Cabins", "Apartments", "Girls Dorm", "Boys Dorm", "RVs"];

const inputStyle = {
  padding: "8px 10px",
  borderRadius: 8,
  border: "1px solid #ddd",
  boxSizing: "border-box",
  width: "100%",
  fontSize: 14
};

const EMPTY_ADD_FORM = {
  camperName: "",
  camperEmail: "",
  bed: "",
  weeklyOwed: "",
  paid: false,
  notes: "",
  showEmail: false
};

function apiFetch(url, token, body) {
  return fetch(url, {
    method: "POST",
    headers: {
      Authorization: `Bearer ${token}`,
      "Content-Type": "application/json"
    },
    body: JSON.stringify(body)
  }).then(async (res) => {
    const json = await res.json().catch(() => ({}));
    if (!res.ok) throw new Error(json?.error || `HTTP ${res.status}`);
    return json;
  });
}

// ─── Room panel ────────────────────────────────────────────────────────────────
function RoomPanel({ area, room, rows, token, onRefresh, registrations }) {
  const [doorCodeInput, setDoorCodeInput] = useState(
    rows.find((r) => String(r.doorCode || "").trim())?.doorCode || ""
  );
  const [doorSaving, setDoorSaving] = useState(false);
  const [doorError, setDoorError] = useState("");

  // Add-camper form state
  const [addForm, setAddForm] = useState(EMPTY_ADD_FORM);
  const [addSaving, setAddSaving] = useState(false);
  const [addError, setAddError] = useState("");

  // Edit row state (one at a time)
  const [editId, setEditId] = useState(null);
  const [editForm, setEditForm] = useState({});
  const [editSaving, setEditSaving] = useState(false);
  const [editError, setEditError] = useState("");

  const [deleteId, setDeleteId] = useState(null);
  const [deleteSaving, setDeleteSaving] = useState(false);
  const [deleteError, setDeleteError] = useState("");

  // Registration picker state
  const [regSearch, setRegSearch] = useState("");
  const datalistId = `reg-dl-${String(area).replace(/\W/g, "-")}-${String(room).replace(/\W/g, "-")}`;

  const getRegName = (r) => (r?.firstName || r?.lastName)
    ? `${r.firstName || ""} ${r.lastName || ""}`.trim()
    : (r?.Name || r?.name || "");

  const getRegOptionLabel = (r) => {
    const name = getRegName(r);
    const email = r?.email || r?.Email || "";
    return email ? `${name} (${email})` : name;
  };

  // Sync doorCode from parent if rows change
  useEffect(() => {
    const dc = rows.find((r) => String(r.doorCode || "").trim())?.doorCode || "";
    setDoorCodeInput(dc);
  }, [rows]);

  const saveDoorCode = async () => {
    setDoorSaving(true);
    setDoorError("");
    try {
      for (const row of rows) {
        await apiFetch("/.netlify/functions/admin-update-assignment", token, {
          id: row.id,
          patch: { doorCode: doorCodeInput }
        });
      }
      await onRefresh();
    } catch (e) {
      setDoorError(String(e?.message || e));
    } finally {
      setDoorSaving(false);
    }
  };

  const addCamper = async () => {
    setAddError("");
    if (!addForm.camperName.trim()) {
      setAddError("Camper name is required.");
      return;
    }
    if (!addForm.bed.trim()) {
      setAddError("Bed is required.");
      return;
    }
    setAddSaving(true);
    try {
      await apiFetch("/.netlify/functions/admin-create-assignment", token, {
        area,
        room,
        bed: addForm.bed.trim(),
        camperName: addForm.camperName.trim(),
        camperEmail: addForm.camperEmail.trim(),
        doorCode: doorCodeInput,
        weeklyOwed: addForm.weeklyOwed.trim(),
        paid: addForm.paid ? "true" : "false",
        notes: addForm.notes.trim()
      });
      setAddForm(EMPTY_ADD_FORM);
      setRegSearch("");
      await onRefresh();
    } catch (e) {
      setAddError(String(e?.message || e));
    } finally {
      setAddSaving(false);
    }
  };

  const startEdit = (row) => {
    setEditId(row.id);
    setEditForm({
      camperName: row.camperName || "",
      camperEmail: row.camperEmail || "",
      bed: row.bed || "",
      weeklyOwed: row.weeklyOwed || "",
      paid: String(row.paid || "").toLowerCase() === "true",
      originalPaid: String(row.paid || "").toLowerCase() === "true",
      notes: row.notes || "",
      showEmail: false
    });
    setEditError("");
  };

  const cancelEdit = () => {
    setEditId(null);
    setEditForm({});
    setEditError("");
  };

  const toAmount = (value) => {
    const num = Number(String(value ?? "").replace(/[^0-9.-]/g, ""));
    return Number.isFinite(num) ? num : 0;
  };

  const maybeAddFinanceIncomeForPaidAssignment = async ({ assignmentId, wasPaid, isPaidNow, form }) => {
    if (wasPaid || !isPaidNow) return;

    const marker = `assignmentId=${assignmentId}`;
    const amount = toAmount(form.weeklyOwed);
    if (amount <= 0) return;

    const financeRes = await fetch("/.netlify/functions/admin-get-finance", {
      headers: { Authorization: `Bearer ${token}` }
    });
    const financeRaw = await financeRes.text();
    let financeJson = {};
    try { financeJson = financeRaw ? JSON.parse(financeRaw) : {}; } catch { financeJson = { error: financeRaw }; }

    if (!financeRes.ok) {
      throw new Error(financeJson?.error || `Failed to check finance entries: ${financeRes.status}`);
    }

    const existingRows = Array.isArray(financeJson?.rows) ? financeJson.rows : [];
    const alreadyExists = existingRows.some((entry) => {
      const notes = String(entry?.notes || entry?.Notes || "").trim();
      return notes.includes(marker);
    });
    if (alreadyExists) return;

    const notesParts = ["Auto from Room Assignments", marker];
    if (room) notesParts.push(`room=${room}`);
    if (form.bed) notesParts.push(`bed=${form.bed.trim()}`);
    if (form.camperEmail) notesParts.push(`email=${form.camperEmail.trim()}`);

    await apiFetch("/.netlify/functions/admin-add-finance", token, {
      date: new Date().toISOString().slice(0, 10),
      type: "income",
      category: "Registration",
      description: "",
      amount: String(amount),
      paidBy: String(form.camperName || "").trim(),
      method: "",
      notes: notesParts.join(" • ")
    });
  };

  const saveEdit = async () => {
    setEditSaving(true);
    setEditError("");
    try {
      const wasPaid = !!editForm.originalPaid;
      const isPaidNow = !!editForm.paid;
      await apiFetch("/.netlify/functions/admin-update-assignment", token, {
        id: editId,
        patch: {
          camperName: editForm.camperName.trim(),
          camperEmail: editForm.camperEmail.trim(),
          bed: editForm.bed.trim(),
          weeklyOwed: editForm.weeklyOwed.trim(),
          paid: editForm.paid ? "true" : "false",
          notes: editForm.notes.trim()
        }
      });

      await maybeAddFinanceIncomeForPaidAssignment({
        assignmentId: editId,
        wasPaid,
        isPaidNow,
        form: editForm
      });

      setEditId(null);
      setEditForm({});
      await onRefresh();
    } catch (e) {
      setEditError(String(e?.message || e));
    } finally {
      setEditSaving(false);
    }
  };

  const deleteRow = async (id) => {
    setDeleteId(id);
    setDeleteSaving(true);
    setDeleteError("");
    try {
      await apiFetch("/.netlify/functions/admin-delete-assignment", token, { id });
      await onRefresh();
    } catch (e) {
      setDeleteError(String(e?.message || e));
    } finally {
      setDeleteSaving(false);
      setDeleteId(null);
    }
  };

  return (
    <div style={{ marginTop: 10 }}>
      {/* Door code */}
      <div style={{ display: "flex", gap: 8, alignItems: "center", flexWrap: "wrap", marginBottom: 10 }}>
        <label style={{ fontSize: 13, fontWeight: 700, whiteSpace: "nowrap" }}>Door code:</label>
        <input
          style={{ ...inputStyle, width: 120 }}
          type="text"
          value={doorCodeInput}
          onChange={(e) => setDoorCodeInput(e.target.value)}
          placeholder="e.g. 1234"
        />
        <button
          className="btn"
          type="button"
          onClick={saveDoorCode}
          disabled={doorSaving || rows.length === 0}
          style={{ minHeight: 36, padding: "6px 14px" }}
        >
          {doorSaving ? "Saving…" : "Save door code"}
        </button>
        {rows.length === 0 && (
          <span className="muted" style={{ fontSize: 12 }}>Add a camper first to save the door code.</span>
        )}
      </div>
      {doorError ? <div className="error" style={{ marginBottom: 8 }}>{doorError}</div> : null}

      {/* Existing campers */}
      {rows.length > 0 && (
        <div style={{ display: "grid", gap: 8, marginBottom: 12 }}>
          {rows.map((row) => {
            const isEditing = editId === row.id;
            if (isEditing) {
              return (
                <div key={row.id} style={{ border: "1px solid #b6d4fe", borderRadius: 10, padding: 10, background: "#f0f7ff" }}>
                  <div style={{ display: "grid", gridTemplateColumns: "1fr 1fr", gap: 8 }}>
                    <div>
                      <div style={{ fontSize: 12, color: "#666", marginBottom: 3 }}>Camper Name *</div>
                      <input
                        style={inputStyle}
                        value={editForm.camperName}
                        onChange={(e) => setEditForm((f) => ({ ...f, camperName: e.target.value }))}
                        placeholder="First Last"
                      />
                    </div>
                    <div>
                      <div style={{ fontSize: 12, color: "#666", marginBottom: 3 }}>Bed *</div>
                      <input
                        style={inputStyle}
                        value={editForm.bed}
                        onChange={(e) => setEditForm((f) => ({ ...f, bed: e.target.value }))}
                        placeholder="e.g. King, Twin 1"
                      />
                    </div>
                    <div>
                      <div style={{ fontSize: 12, color: "#666", marginBottom: 3 }}>Weekly Owed ($)</div>
                      <input
                        style={inputStyle}
                        type="number"
                        min="0"
                        step="0.01"
                        value={editForm.weeklyOwed}
                        onChange={(e) => setEditForm((f) => ({ ...f, weeklyOwed: e.target.value }))}
                        placeholder="0.00"
                      />
                    </div>
                    <div style={{ display: "flex", alignItems: "center", gap: 8, paddingTop: 18 }}>
                      <label style={{ fontWeight: 700, fontSize: 13, cursor: "pointer" }}>
                        <input
                          type="checkbox"
                          checked={editForm.paid}
                          onChange={(e) => setEditForm((f) => ({ ...f, paid: e.target.checked }))}
                          style={{ marginRight: 5 }}
                        />
                        Paid
                      </label>
                    </div>
                    <div style={{ gridColumn: "1 / -1" }}>
                      <div style={{ fontSize: 12, color: "#666", marginBottom: 3 }}>Notes</div>
                      <input
                        style={inputStyle}
                        value={editForm.notes}
                        onChange={(e) => setEditForm((f) => ({ ...f, notes: e.target.value }))}
                        placeholder="Optional notes"
                      />
                    </div>
                    <div style={{ gridColumn: "1 / -1" }}>
                      <button
                        type="button"
                        style={{ background: "none", border: "none", cursor: "pointer", color: "#666", fontSize: 12, padding: 0 }}
                        onClick={() => setEditForm((f) => ({ ...f, showEmail: !f.showEmail }))}
                      >
                        {editForm.showEmail ? "▲ Hide email" : "▼ Show email (optional)"}
                      </button>
                      {editForm.showEmail && (
                        <div style={{ marginTop: 6 }}>
                          <input
                            style={inputStyle}
                            type="email"
                            value={editForm.camperEmail}
                            onChange={(e) => setEditForm((f) => ({ ...f, camperEmail: e.target.value }))}
                            placeholder="camper@email.com (optional)"
                          />
                        </div>
                      )}
                    </div>
                  </div>

                  {editError ? <div className="error" style={{ marginTop: 6 }}>{editError}</div> : null}

                  <div style={{ display: "flex", gap: 8, marginTop: 10 }}>
                    <button
                      className="btn primary"
                      type="button"
                      onClick={saveEdit}
                      disabled={editSaving}
                      style={{ minHeight: 36, padding: "6px 14px" }}
                    >
                      {editSaving ? "Saving…" : "Save"}
                    </button>
                    <button
                      className="btn"
                      type="button"
                      onClick={cancelEdit}
                      disabled={editSaving}
                      style={{ minHeight: 36, padding: "6px 14px" }}
                    >
                      Cancel
                    </button>
                  </div>
                </div>
              );
            }

            const isPaid = String(row.paid || "").toLowerCase() === "true";
            const isDeleting = deleteId === row.id && deleteSaving;

            return (
              <div
                key={row.id}
                style={{
                  border: "1px solid #f0f0f0",
                  borderRadius: 10,
                  padding: "8px 12px",
                  display: "flex",
                  justifyContent: "space-between",
                  alignItems: "flex-start",
                  gap: 10,
                  flexWrap: "wrap",
                  background: "#fafafa"
                }}
              >
                <div>
                  <div style={{ fontWeight: 800, fontSize: 14 }}>{row.camperName || "Unknown"}</div>
                  <div className="muted" style={{ fontSize: 12 }}>
                    {row.bed ? `Bed: ${row.bed}` : "No bed set"}
                    {row.weeklyOwed ? ` • Owes: $${row.weeklyOwed}` : ""}
                    {" • "}
                    <span style={{ color: isPaid ? "#067647" : "#b42318", fontWeight: 700 }}>
                      {isPaid ? "✓ Paid" : "Unpaid"}
                    </span>
                    {row.notes ? ` • ${row.notes}` : ""}
                  </div>
                </div>
                <div style={{ display: "flex", gap: 6, flexShrink: 0 }}>
                  <button
                    className="btn"
                    type="button"
                    onClick={() => startEdit(row)}
                    style={{ minHeight: 32, padding: "4px 12px", fontSize: 13 }}
                  >
                    Edit
                  </button>
                  <button
                    className="btn"
                    type="button"
                    onClick={() => { if (window.confirm(`Delete ${row.camperName || "this camper"}?`)) deleteRow(row.id); }}
                    disabled={isDeleting}
                    style={{ minHeight: 32, padding: "4px 12px", fontSize: 13, background: "#fee2e2", color: "#b42318" }}
                  >
                    {isDeleting ? "…" : "Delete"}
                  </button>
                </div>
              </div>
            );
          })}
          {deleteError ? <div className="error">{deleteError}</div> : null}
        </div>
      )}

      {/* Add camper form */}
      <details style={{ border: "1px dashed #b6d4fe", borderRadius: 10, padding: 10 }}>
        <summary style={{ cursor: "pointer", fontWeight: 700, fontSize: 14, color: "#1a73e8" }}>
          + Add camper to this room
        </summary>
        <div style={{ marginTop: 10, display: "grid", gridTemplateColumns: "1fr 1fr", gap: 8 }}>
          {registrations && registrations.length > 0 && (
            <div style={{ gridColumn: "1 / -1" }}>
              <div style={{ fontSize: 12, color: "#666", marginBottom: 3 }}>Pick from registrations (optional)</div>
              <input
                style={inputStyle}
                list={datalistId}
                placeholder="Search by name or email…"
                value={regSearch}
                onChange={(e) => {
                  const val = e.target.value;
                  setRegSearch(val);
                  const match = registrations.find((r) => getRegOptionLabel(r) === val);
                  if (match) {
                    const email = match?.email || match?.Email || "";
                    setAddForm((f) => ({ ...f, camperName: getRegName(match), camperEmail: email }));
                  }
                }}
              />
              <datalist id={datalistId}>
                {registrations.map((r, i) => (
                  <option key={r._id || r.email || r.Email || i} value={getRegOptionLabel(r)} />
                ))}
              </datalist>
            </div>
          )}
          <div>
            <div style={{ fontSize: 12, color: "#666", marginBottom: 3 }}>Camper Name *</div>
            <input
              style={inputStyle}
              value={addForm.camperName}
              onChange={(e) => setAddForm((f) => ({ ...f, camperName: e.target.value }))}
              placeholder="First Last"
            />
          </div>
          <div>
            <div style={{ fontSize: 12, color: "#666", marginBottom: 3 }}>Bed *</div>
            <input
              style={inputStyle}
              value={addForm.bed}
              onChange={(e) => setAddForm((f) => ({ ...f, bed: e.target.value }))}
              placeholder="e.g. King, Twin 1, Couch"
            />
          </div>
          <div>
            <div style={{ fontSize: 12, color: "#666", marginBottom: 3 }}>Weekly Owed ($)</div>
            <input
              style={inputStyle}
              type="number"
              min="0"
              step="0.01"
              value={addForm.weeklyOwed}
              onChange={(e) => setAddForm((f) => ({ ...f, weeklyOwed: e.target.value }))}
              placeholder="0.00"
            />
          </div>
          <div style={{ display: "flex", alignItems: "center", gap: 8, paddingTop: 18 }}>
            <label style={{ fontWeight: 700, fontSize: 13, cursor: "pointer" }}>
              <input
                type="checkbox"
                checked={addForm.paid}
                onChange={(e) => setAddForm((f) => ({ ...f, paid: e.target.checked }))}
                style={{ marginRight: 5 }}
              />
              Paid
            </label>
          </div>
          <div style={{ gridColumn: "1 / -1" }}>
            <div style={{ fontSize: 12, color: "#666", marginBottom: 3 }}>Notes</div>
            <input
              style={inputStyle}
              value={addForm.notes}
              onChange={(e) => setAddForm((f) => ({ ...f, notes: e.target.value }))}
              placeholder="Optional notes"
            />
          </div>
          <div style={{ gridColumn: "1 / -1" }}>
            <button
              type="button"
              style={{ background: "none", border: "none", cursor: "pointer", color: "#666", fontSize: 12, padding: 0 }}
              onClick={() => setAddForm((f) => ({ ...f, showEmail: !f.showEmail }))}
            >
              {addForm.showEmail ? "▲ Hide email" : "▼ Show email (optional)"}
            </button>
            {addForm.showEmail && (
              <div style={{ marginTop: 6 }}>
                <input
                  style={inputStyle}
                  type="email"
                  value={addForm.camperEmail}
                  onChange={(e) => setAddForm((f) => ({ ...f, camperEmail: e.target.value }))}
                  placeholder="camper@email.com (optional)"
                />
              </div>
            )}
          </div>
        </div>

        {addError ? <div className="error" style={{ marginTop: 6 }}>{addError}</div> : null}

        <div style={{ display: "flex", gap: 8, marginTop: 10 }}>
          <button
            className="btn primary"
            type="button"
            onClick={addCamper}
            disabled={addSaving}
            style={{ minHeight: 36, padding: "6px 14px" }}
          >
            {addSaving ? "Adding…" : "Add camper"}
          </button>
          <button
            className="btn"
            type="button"
            onClick={() => { setAddForm(EMPTY_ADD_FORM); setRegSearch(""); }}
            disabled={addSaving}
            style={{ minHeight: 36, padding: "6px 14px" }}
          >
            Clear
          </button>
        </div>
      </details>
    </div>
  );
}

// ─── Area panel ────────────────────────────────────────────────────────────────
function AreaPanel({ area, roomMap, token, onRefresh, registrations }) {
  const [newRoomName, setNewRoomName] = useState("");
  const [newRoomError, setNewRoomError] = useState("");
  const [addRoomOpen, setAddRoomOpen] = useState(false);

  const roomNames = useMemo(() => {
    return Object.keys(roomMap).sort((a, b) => a.localeCompare(b, undefined, { numeric: true }));
  }, [roomMap]);

  const totalCampers = roomNames.reduce((n, r) => n + roomMap[r].length, 0);

  const addRoom = () => {
    setNewRoomError("");
    const name = newRoomName.trim();
    if (!name) {
      setNewRoomError("Room name is required.");
      return;
    }
    if (roomMap[name]) {
      setNewRoomError(`Room "${name}" already exists.`);
      return;
    }
    // Adding a room opens it in a "phantom" state — the room will actually be created
    // once the user adds the first camper via the RoomPanel add-camper form.
    // We signal this by adding an empty entry. The parent passes onRefresh so the
    // room will disappear again if no campers are saved.
    setNewRoomName("");
    setAddRoomOpen(false);
    // We cannot mutate roomMap directly; instead tell parent to inject a local room.
    onRefresh({ pendingRoom: { area, room: name } });
  };

  return (
    <div style={{ display: "grid", gap: 8 }}>
      {roomNames.length === 0 && (
        <div className="muted" style={{ fontSize: 13 }}>
          No rooms yet. Use "+ Add room" below to create one.
        </div>
      )}

      {roomNames.map((room) => {
        const people = roomMap[room] || [];
        const doorCode = people.find((p) => String(p.doorCode || "").trim())?.doorCode || "";
        const totalOwed = people.reduce((s, p) => s + (parseFloat(p.weeklyOwed) || 0), 0);
        const paidCount = people.filter((p) => String(p.paid || "").toLowerCase() === "true").length;

        return (
          <details key={room} style={{ border: "1px solid #e8edf3", borderRadius: 10, padding: 10 }}>
            <summary style={{ cursor: "pointer", fontWeight: 800, fontSize: 14 }}>
              {room}{" "}
              <span className="muted" style={{ fontWeight: 400 }}>
                ({people.length} {people.length === 1 ? "camper" : "campers"})
                {doorCode ? ` • Door: ${doorCode}` : ""}
                {totalOwed > 0 ? ` • $${totalOwed.toFixed(2)} owed` : ""}
                {people.length > 0 ? ` • ${paidCount}/${people.length} paid` : ""}
              </span>
            </summary>
            <RoomPanel
              area={area}
              room={room}
              rows={people}
              token={token}
              onRefresh={onRefresh}
              registrations={registrations}
            />
          </details>
        );
      })}

      {/* Add room */}
      {addRoomOpen ? (
        <div style={{ display: "flex", gap: 8, alignItems: "center", flexWrap: "wrap", marginTop: 4 }}>
          <input
            style={{ ...inputStyle, width: 200 }}
            value={newRoomName}
            onChange={(e) => setNewRoomName(e.target.value)}
            placeholder="Room name, e.g. Room 1"
            onKeyDown={(e) => { if (e.key === "Enter") addRoom(); if (e.key === "Escape") setAddRoomOpen(false); }}
            autoFocus
          />
          <button
            className="btn primary"
            type="button"
            onClick={addRoom}
            style={{ minHeight: 36, padding: "6px 14px" }}
          >
            Add room
          </button>
          <button
            className="btn"
            type="button"
            onClick={() => { setAddRoomOpen(false); setNewRoomName(""); setNewRoomError(""); }}
            style={{ minHeight: 36, padding: "6px 14px" }}
          >
            Cancel
          </button>
          {newRoomError ? <div className="error" style={{ fontSize: 13 }}>{newRoomError}</div> : null}
        </div>
      ) : (
        <button
          className="btn"
          type="button"
          onClick={() => setAddRoomOpen(true)}
          style={{ minHeight: 36, padding: "6px 14px", alignSelf: "flex-start", marginTop: 4 }}
        >
          + Add room
        </button>
      )}

      <div className="muted" style={{ fontSize: 11, marginTop: 2 }}>
        {totalCampers} camper{totalCampers !== 1 ? "s" : ""} in {roomNames.length} room{roomNames.length !== 1 ? "s" : ""}
      </div>
    </div>
  );
}

// ─── Main component ────────────────────────────────────────────────────────────
export default function AdminAssignments({ token, registrations }) {
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState("");
  const [assignments, setAssignments] = useState([]);

  // Local "pending rooms" created from UI before first camper is added
  const [pendingRooms, setPendingRooms] = useState([]); // [{area, room}]

  const fetchAssignments = useCallback(async (opts) => {
    if (opts && opts.pendingRoom) {
      setPendingRooms((prev) => {
        const key = `${opts.pendingRoom.area}|||${opts.pendingRoom.room}`;
        if (prev.some((p) => `${p.area}|||${p.room}` === key)) return prev;
        return [...prev, opts.pendingRoom];
      });
      return;
    }
    setLoading(true);
    setError("");
    try {
      const res = await fetch("/.netlify/functions/admin-get-assignments", {
        headers: { Authorization: `Bearer ${token}` }
      });
      const data = await res.json();
      if (!res.ok) throw new Error(data?.error || "Failed to load assignments");
      const rows = data.rows || [];
      setAssignments(rows);
      // Remove pending rooms that now have real data
      setPendingRooms((prev) =>
        prev.filter(
          (p) =>
            !rows.some(
              (r) =>
                String(r.area || "").trim() === p.area &&
                String(r.room || "").trim() === p.room
            )
        )
      );
    } catch (e) {
      setError(String(e?.message || e));
    } finally {
      setLoading(false);
    }
  }, [token]);

  useEffect(() => {
    if (token) fetchAssignments();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [token]);

  // Group assignments by area -> room
  const grouped = useMemo(() => {
    const g = {};
    for (const item of assignments) {
      const area = String(item.area || "").trim() || "Unassigned";
      const room = String(item.room || "").trim() || "Unassigned";
      if (!g[area]) g[area] = {};
      if (!g[area][room]) g[area][room] = [];
      g[area][room].push(item);
    }
    // Inject pending rooms (empty arrays) so the UI shows them
    for (const p of pendingRooms) {
      if (!g[p.area]) g[p.area] = {};
      if (!g[p.area][p.room]) g[p.area][p.room] = [];
    }
    return g;
  }, [assignments, pendingRooms]);

  // Areas: fixed list first, then any extras found in data
  const sortedAreas = useMemo(() => {
    const dataAreas = Object.keys(grouped);
    return [...new Set([...FIXED_AREAS, ...dataAreas])];
  }, [grouped]);

  return (
    <div style={{ marginTop: 14 }}>
      <div style={{ display: "flex", gap: 10, alignItems: "center", flexWrap: "wrap" }}>
        <h2 style={{ margin: 0 }}>Room Assignments</h2>
        <button className="btn" type="button" onClick={() => fetchAssignments()} disabled={loading}>
          {loading ? "Loading…" : "Refresh"}
        </button>
      </div>

      {error ? <div className="error" style={{ marginTop: 10 }}>{error}</div> : null}

      <div style={{ display: "grid", gap: 12, marginTop: 14 }}>
        {sortedAreas.map((area) => {
          const roomMap = grouped[area] || {};
          const hasRooms = Object.keys(roomMap).length > 0;

          return (
            <details key={area} style={{ border: "1px solid #dde3ec", borderRadius: 12, padding: 14, background: "#fff" }} open={hasRooms}>
              <summary style={{ cursor: "pointer", fontWeight: 900, fontSize: 16 }}>
                {area}
                {" "}
                <span className="muted" style={{ fontWeight: 400 }}>
                  ({Object.values(roomMap).reduce((n, r) => n + r.length, 0)} campers)
                </span>
              </summary>

              <div style={{ marginTop: 10 }}>
                <AreaPanel
                  area={area}
                  roomMap={roomMap}
                  token={token}
                  onRefresh={fetchAssignments}
                  registrations={registrations}
                />
              </div>
            </details>
          );
        })}
      </div>
    </div>
  );
}
